import React, { useEffect, useRef, useState } from "react";
import { HttpStatusCode } from "axios";
import { useNavigate } from "react-router-dom";

import { findCategoryForContext } from "../../libs/category";

import { CATEGORY, CATEGORY_ALIAS } from "../../types/talk";

import { SERVICE_TEMPLATE_ID } from "../../consts/serviceTemplate";

import { TalkCategory } from "../../services/talkServices";
import { getNexpaInfo, getSandiMarketUrl } from "../../services/memberServices";
import { logout } from "../../services/authServices";

import { useUserContext } from "../../contexts/userContext";
import { useSandiTalk } from "../../contexts/boardContext";

import useShowActivity from "../useShowActivity";
import useSimplePopup from "../useSimplePopup";
import useStore from "../useStore";
import { useDeviceType } from "../useDeviceType/Index";

import { TotalServiceWrapper } from "../../pages/TotalService";
import NeedCertCompanyPopup from "../../components/Popup/NeedCertCompany";
import useFullScreenLoading from "../useFullScreenLoading";
import { getSmartOfficeEncryptionInfo } from "../../services/smartOfficeServices";
import { SMART_OFFICE_ERROR_CODE, SMART_OFFICE_ERROR_MESSAGE } from "../../consts/code";
import { useSmartOffice } from "../useSmartOffice";
import useAppVersion from "../useAppVersion";
import { AppVersion, getAppVersion } from "../../services/etcServices";

// 전역 변수로 서버에서 받아온 OS 타입과 버전 정보 저장
export let serverOsType: string = "";
export let serverVersion: string = "";

// 전역 변수를 업데이트하는 함수
export const updateServerVersionInfo = (osType: string, version: string) => {
  serverOsType = osType;
  serverVersion = version;
  console.log("Server version info updated:", { osType, version });
};

type Props = {};

// famous 샌디홈 클릭시 아이콘 사용 여부 판단하는 훅
export default function useNavigateService(
  props: Props
): [
  (
    service: TotalServiceWrapper,
    buildingCategoryList?: TalkCategory[],
    activeServiceTab?: string,
    onNavigate?: () => void
  ) => void,
  JSX.Element
] {
  const [popup, setPopup] = useState<"needCertCompany" | "">("");
  const [simplePopupComponent, { open, openError }] = useSimplePopup({});
  const navigate = useNavigate();
  const [user] = useUserContext();
  const [talk, { updateCategory }] = useSandiTalk();
  const selectedBuilding = {
    buildingId: user.buildingId,
    buildingName: user.buildingName,
    buildingFloorId: user.buildingFloorId,
    buildingFloorName: user.buildingFloorName,
    buildingSpaceId: user.buildingSpaceId,
    buildingSpaceName: user.buildingSpaceName,
  };
  const [storeCall] = useStore({ open });
  const deviceType = useDeviceType();
  const [marketUrl, setMarketUrl] = useState("");
  const [nexpaQueryString, setNexpaQueryString] = useState<string>("");
  const prevUpdateTime = useRef<Date | null>(null);
  const prevNexpaUpdateTime = useRef<Date | null>(null);
  const [showActivityCall] = useShowActivity({ open });
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const loadingComponent = useFullScreenLoading({
    isLoading,
  });

  const [appVersionResult, versionCall] = useAppVersion();
  const {failedRedirect } = useSmartOffice();
  
  const loadMarketUrl = async () => {
    try {
      const result = await getSandiMarketUrl();
      if (result.status === HttpStatusCode.Ok) {
        setMarketUrl(result.data);
        prevUpdateTime.current = new Date();
      }
    } catch (e: any) {
      if (e?.response?.status === HttpStatusCode.Unauthorized) {
        logout();
      } else if (e?.response?.data?.message) {
        open(e.response.data.message?.replace(". ", ".\n"));
      } else {
        open("샌디 마켓 정보를 가져오는데 실패했습니다.");
      }
    }
  };

  const loadNexpaInfo = async () => {
    try {
      const response = await getNexpaInfo();
      if (response.status === HttpStatusCode.Ok) {
        const params = {
          employeeId: response.data.employeeId,
          email: response.data.email,
          key: response.data.key,
          tel: response.data.tell,
        };
        const queryString = new URLSearchParams(params).toString();
        setNexpaQueryString(queryString);
        prevNexpaUpdateTime.current = new Date();
      } else {
        open(
          "넥스파 주차 연동 정보 조회에 실패하였습니다.\n관리자에게 문의해주세요."
        );
      }
    } catch (e: any) {
      if (e?.response?.status === HttpStatusCode.Unauthorized) {
        logout();
      } else if (e?.response?.data?.message) {
        open(e.response.data.message?.replace(". ", ".\n"));
      } else {
        open("넥스파 주차 연동 정보 조회에 실패했습니다.");
      }
    }
  };

  const getSmartOfficeEnc = async () => {
    try {
      const response = await getSmartOfficeEncryptionInfo();
      if (response.status === HttpStatusCode.Ok) {
        return response.data
      } 
    } catch (e: any) {
      if (e?.response?.status === HttpStatusCode.Unauthorized) {
        logout(); 
      } else if (e?.response?.data?.code === SMART_OFFICE_ERROR_CODE.NOT_VERIFIED_COMPANY) {
        open(SMART_OFFICE_ERROR_MESSAGE.NOT_VERIFIED_COMPANY, {onClick: () => failedRedirect(SMART_OFFICE_ERROR_CODE.NOT_VERIFIED_COMPANY) , enableDimmedClose:false});
      } else if (e?.response?.data?.code === SMART_OFFICE_ERROR_CODE.INVALIDE_SMARTOFFICE_BUILDING) {
        open(SMART_OFFICE_ERROR_MESSAGE.INVALIDE_SMARTOFFICE_BUILDING, {onClick: () => failedRedirect(SMART_OFFICE_ERROR_CODE.INVALIDE_SMARTOFFICE_BUILDING) , enableDimmedClose:false});
      } else if (e?.response?.data?.message) {
        open(e.response.data.message?.replace(". ", ".\n"));
      } else {
        open("스마트 오피스 연동 정보 조회에 실패했습니다.");
      }
    }
    return null
  };

// 2025-03-28 famous inner webview 추가
// innerWebView를 호출하는 내부 함수
  const handleInnerWebView = (service: TotalServiceWrapper, serviceName: string | undefined, url: string | null | undefined) => {        
    // const sandiAppVersion = appVersionResult?.result || "unknown";
    
    // 버전 비교를 위한 함수
    // const compareVersions = (v1: string, v2: string): number => {
    //   const parts1 = v1.split('.').map(Number);
    //   const parts2 = v2.split('.').map(Number);
      
    //   for (let i = 0; i < Math.max(parts1.length, parts2.length); i++) {
    //     const part1 = parts1[i] || 0;
    //     const part2 = parts2[i] || 0;
        
    //     if (part1 > part2) return 1;
    //     if (part1 < part2) return -1;
    //   }
      
    //   return 0;
    // };
  
    // 서버 버전이 네이티브 버전보다 큰지 확인  
    // const serverversion = serverVersion || "0.0.0";
    // const nativeVersion = sandiAppVersion || "0.0.0";
    // const isServerVersionHigher = compareVersions(serverversion, nativeVersion) > 0;

    // console.log("isServerVersionHigher : ", isServerVersionHigher);
    // console.log("serverversion : ", serverversion);
    // console.log("nativeVersion : ", nativeVersion);
    // 서버 버전이 네이티브 버전보다 크면 navigate 호출
    // if (isServerVersionHigher) {
    //   navigate("/service/custom", { state: service });
    //   return;
    // }
    
    const varUA = navigator.userAgent.toLowerCase();
    if (varUA.indexOf("android") > -1) {
      
      if (window.SANDI && window.SANDI.innerWebView) {
        try {
          window.SANDI.innerWebView(serviceName, url);
        } catch (error) {        
          navigate("/service/custom", { state: service });
        }
      } else {
        navigate("/service/custom", { state: service });
      }
    } else if (
      varUA.indexOf("iphone") > -1 ||
      varUA.indexOf("ipad") > -1 ||
      varUA.indexOf("ipod") > -1
    ) {      
      if (window.webkit?.messageHandlers?.innerWebView) {
        try {
          window.webkit.messageHandlers.innerWebView.postMessage({title: serviceName, url: url || ""});
        } catch (error) { 
          navigate("/service/custom", { state: service });
        }
      } else {
        navigate("/service/custom", { state: service });
      }
    } else {
      navigate("/service/custom", { state: service });
    }
  };


  
  useEffect(() => {
    // versionCall();    
    loadMarketUrl();
    loadNexpaInfo();

    const interval = setInterval(() => {
      if (
        prevUpdateTime?.current &&
        new Date().getTime() - prevUpdateTime.current.getTime() >=
          25 * 60 * 1000
      ) {
        loadMarketUrl();
      }
    }, 60000);

    const intervalNexpa = setInterval(() => {
      if (
        prevNexpaUpdateTime?.current &&
        new Date().getTime() - prevNexpaUpdateTime.current.getTime() >=
          25 * 60 * 1000
      ) {
        loadNexpaInfo();
      }
    }, 60000);

  
    return () => {
      clearInterval(interval);
      clearInterval(intervalNexpa);
    };
  }, []);

  // useEffect(() => {
  //   console.log("useNavigateService getAppVersion : ", appVersionResult);
  //   console.log("useNavigateService deviceType : ", deviceType);
  //   getAppVersion(deviceType, appVersionResult.result || "").then((res) => {
  //     if (res.status === HttpStatusCode.Ok) {
  //       setServerAppVersion(res.data);
        
  //       // 전역 변수 업데이트
  //       updateServerVersionInfo(res.data.osType, res.data.version);
  //     } else {
  //       setServerAppVersion(null);  
  //     }
  //   });
  // }, [appVersionResult, deviceType]);

  const updateCategoryFromLocation = async (categoryId: number) => {
    const categoryInfo = findCategoryForContext(
      talk?.categoryList ?? [],
      "lifeCategoryId",
      categoryId,
      0
    );
    if (categoryInfo) {
      await updateCategory({
        categoryInfo: categoryInfo,
      });
    } else {
      console.log("categoryInfo is null");
    }
  };

  const navigateService = async (
    service: TotalServiceWrapper,
    buildingCategoryList?: TalkCategory[],
    activeServiceTab?: string,
    onNavigate?: () => void
  ) => {
    if (!service.serviceId) {
      open(
        `앗! 아직 해당기능이 적용되지 않았어요!\n도입이 필요하시다면 "도와주세요"로 알려주세요`
      );
      return;
    }

    if (service.serviceStatus === "only-occupant") {
      open("앗, 해당기능은 건물 입주사만 사용가능해요!");
      return;
    }

    if (service.needAuthorized === true) {
      setPopup("needCertCompany");
      return;
    }

    if (service.serviceStatus === "setting") {
      open("설정이 진행중인 서비스예요.\n관리자에게 문의해주세요.");
      return;
    }

    if (
      [SERVICE_TEMPLATE_ID.ETC_PARKING, SERVICE_TEMPLATE_ID.DANGEROUS].includes(
        service.serviceTemplateId ?? -1
      )
    ) {
      navigate("/report", {
        state: {
          serviceTemplateId: service.serviceTemplateId,
        },
      });
      return;
    }

    if (service.serviceTemplateId === SERVICE_TEMPLATE_ID.BUILDING_MENU) {
      if (
        buildingCategoryList &&
        (buildingCategoryList.length === 0 ||
          !buildingCategoryList.find(
            (category) => category.alias === CATEGORY_ALIAS.BUILDING_MENU
          ))
      ) {
        open("건물에 설정되지않은 게시판이예요.\n관리자에게 문의해주세요.");
        return;
      }
      updateCategoryFromLocation(CATEGORY.BUILDING_MENU);
      navigate("/talk", {
        state: { ...selectedBuilding, lifeCategoryId: CATEGORY.BUILDING_MENU },
      });
      return;
    }

    if (service.serviceTemplateId === SERVICE_TEMPLATE_ID.SANDI_MARKET) {
      if (marketUrl) {
        window.open(marketUrl);
      } else {
        open("샌디 마켓 정보를 가져오는데 실패했습니다.");
      }
      return;
    }

    if (
      service.serviceItemType &&
      service.serviceItemType === "NEXPA_PARKING"
    ) {
      const url = service.actionJson?.[0].link;
      if (url) {
        try {
          if (nexpaQueryString) {
            const fullUrl = `${url}?${nexpaQueryString}`;
            window.open(fullUrl);
          } else {
            open(
              "넥스파 주차 연동 정보 조회에 실패하였습니다.\n관리자에게 문의해주세요."
            );
          }
        } catch (err: any) {
          openError(err, "넥스파 주차 서비스 실행 중 오류가 발생했습니다.");
        } finally {
          return;
        }
      } else {
        open("데이터가 유효하지 않습니다.");
        return;
      }
    }

    if (service.serviceItemType && service.serviceItemType === "SMART_OFFICE") {
      const url = service.actionJson?.[0].link;
      if (url) {
        try {
          const enc = await getSmartOfficeEnc();
          if (enc) {
            const fullUrl = `${url}?enc=${enc.enc}`;
            showActivityCall("스마트 오피스", fullUrl);
          } 
        } catch (err: any) {
          openError(err, "스마트 오피스 서비스 실행 중 오류가 발생했습니다.");
        } finally {
          return;
        }
      } else {
        open("데이터가 유효하지 않습니다.");
        return;
      }
    }

    if (
      service.serviceType === "CUSTOM" ||
      service.serviceType === "MANAGER_CUSTOM"
    ) {
      if (service.action === "LINK") {
        if ((service?.actionJson?.length ?? 0) <= 0) {
          open("링크 경로 정보가 없습니다.");
        } else if (service?.actionJson?.[0].action === "phone") {
          window.location.href = `tel:${service?.actionJson?.[0].link}`;
        } else if (service?.actionJson?.[0].action === "web") {
          if (service?.actionJson?.[0].target === "outer") {
            window.open(service?.actionJson?.[0]?.link ?? "", "_blank");
          } else if (service?.actionJson?.[0].target === "inner") {
            navigate("/service/custom", { state: service });
            // handleInnerWebView(service, service?.serviceName, service?.actionJson?.[0]?.link);
          } else {
            open(
              `미지원 링크입니다. (actionJson.target: ${service?.actionJson?.[0].target})`
            );
          }
        } else if (
          service?.actionJson?.[0].action === "" &&
          service?.actionJson?.[0].target === "inner"
        ) {
          navigate("/service/custom", { state: service });
          // handleInnerWebView(service, service?.serviceName, service?.actionJson?.[0]?.link);
        } else if (service?.actionJson?.[0].action === "app") {
          if (deviceType === "Android") {
            if (service?.actionJson?.[0]?.storeAndroid) {
              storeCall(
                service?.actionJson?.[0]?.schemeAndroid,
                service?.actionJson?.[0]?.storeAndroid
              );
            } else {
              open("안드로이드 앱 정보가 없습니다.");
            }
          } else if (deviceType === "iOS") {
            if (service?.actionJson?.[0]?.storeIos) {
              storeCall(
                service?.actionJson?.[0]?.schemeIos,
                service?.actionJson?.[0]?.storeIos
              );
            } else {
              open("iOS 앱 정보가 없습니다.");
            }
          } else {
            open("데스크탑에서는 앱 연동은 미지원 상태입니다.");
          }
        } else if (service?.actionJson?.[0].action === "activity") {
          showActivityCall(
            service.serviceName,
            service?.actionJson?.[0]?.link ?? ""
          );
        } else {
          open(
            `미지원 서비스입니다. (actionJson.action: ${service?.actionJson?.[0].action})`
          );
        }
      } else if (service.action === "INFO") {
        navigate("/service/custom", { state: service });
      } else {
        open(`미지원 서비스입니다. (action: ${service.action})`);
      }
      return;
    }

    if (service.serviceDetailType) {
      if (onNavigate) {
        onNavigate();
      } else {
        navigate(
          `/${
            activeServiceTab ?? "service"
          }/${service.serviceDetailType.toLowerCase()}`,
          { state: selectedBuilding }
        );
      }
      return;
    }

    open("분류되지 않은 서비스예요.");
  };

  return [
    navigateService,
    <>
      <NeedCertCompanyPopup popup={popup} onClose={() => setPopup("")} />
      {simplePopupComponent}
      {loadingComponent}
    </>,
  ];
}
